import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { FaCheckCircle, FaTimesCircle, FaSearch, FaChartLine } from 'react-icons/fa';
import './StudentProgress.css';

const StudentProgress = ({ courseId }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchStudentsProgress = async () => {
            setLoading(true);
            setError(null);
            try {
                const auth = getAuth();
                const user = auth.currentUser;
                if (!user) throw new Error('User not authenticated');

                const token = await user.getIdToken();
                const response = await axios.get(
                    `/api/progress/courses/${courseId}/students-progress`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                setData(response.data);
            } catch (err) {
                console.error('Error fetching students progress:', err);
                setError(err.response?.data?.message || 'Failed to load student progress');
            } finally {
                setLoading(false);
            }
        };

        fetchStudentsProgress();
    }, [courseId]);

    const filteredStudents = data?.students?.filter(student =>
        student.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.student_email.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    if (loading) {
        return (
            <div className="student-progress-container">
                <div className="loading-spinner">⏳ جارٍ التحميل...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="student-progress-container">
                <div className="alert alert-error">❌ {error}</div>
            </div>
        );
    }

    if (!data || data.total_students === 0) {
        return (
            <div className="student-progress-container">
                <div className="alert alert-info">ℹ️ لا يوجد طلاب مسجلين في هذه الدورة بعد.</div>
            </div>
        );
    }

    return (
        <div className="student-progress-container">
            <div className="progress-header">
                <div className="header-title">
                    <FaChartLine className="title-icon" />
                    <h2>تقدم الطلاب</h2>
                </div>
                <div className="student-count-badge">
                    إجمالي الطلاب: <strong>{data.total_students}</strong>
                </div>
            </div>

            <div className="search-box">
                <FaSearch className="search-icon" />
                <input
                    type="text"
                    placeholder="ابحث عن طالب بالاسم أو البريد الإلكتروني..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
            </div>

            <div className="table-container">
                <table className="progress-table">
                    <thead>
                        <tr>
                            <th>الطالب</th>
                            <th>البريد الإلكتروني</th>
                            <th>نسبة الإنجاز</th>
                            <th>المحاضرات</th>
                            <th>الامتحانات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredStudents.map((student) => {
                            const progressClass =
                                student.progress_percent >= 70 ? 'success' :
                                    student.progress_percent >= 40 ? 'warning' : 'danger';

                            return (
                                <tr key={student.student_id}>
                                    <td data-label="الطالب">
                                        <strong>{student.student_name}</strong>
                                    </td>
                                    <td className="email-cell" data-label="البريد">
                                        {student.student_email}
                                    </td>
                                    <td data-label="نسبة الإنجاز">
                                        <div className="progress-cell">
                                            <span className="progress-percent">{student.progress_percent}%</span>
                                            <div className="progress-bar-container">
                                                <div
                                                    className={`progress-bar ${progressClass}`}
                                                    style={{ width: `${student.progress_percent}%` }}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="center" data-label="المحاضرات">
                                        <div className="lectures-cell">
                                            <span className={`badge ${student.completed_lectures === student.total_lectures ? 'badge-success' : 'badge-default'}`}>
                                                {student.completed_lectures}/{student.total_lectures}
                                            </span>
                                            <div className="lectures-list-mini">
                                                {student.completed_lectures_list?.map(lecture => (
                                                    <div key={lecture.id} className="lecture-item-name">
                                                        <FaCheckCircle className="icon-success-small" />
                                                        <span>{lecture.title}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </td>
                                    <td data-label="الامتحانات">
                                        <div className="exams-cell">
                                            {student.exams.length === 0 ? (
                                                <span className="badge badge-outline">لم يحل بعد</span>
                                            ) : (
                                                student.exams.map((exam) => (
                                                    <div key={exam.exam_id} className="exam-result">
                                                        {exam.passed ? (
                                                            <FaCheckCircle className="icon-success" />
                                                        ) : (
                                                            <FaTimesCircle className="icon-error" />
                                                        )}
                                                        <span className="exam-info">
                                                            {exam.exam_title} ({(Number(exam.percent) || 0).toFixed(0)}%)
                                                        </span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {filteredStudents.length === 0 && searchTerm && (
                <div className="alert alert-info">
                    لم يتم العثور على طلاب يطابقون البحث "{searchTerm}"
                </div>
            )}
        </div>
    );
};

export default StudentProgress;
